// The picture side of the video background changer. For every frame:
//   1. a mask says how much each pixel is the subject (0..1): from the AI model (a person), or from a green / blue screen (the distance of each colour from the key colour);
//   2. the mask is cleaned: its edges are snapped to the edges of the real picture (a joint bilateral filter, because the model's mask is small and soft), and it is blended with the
//      mask of the frame before it, so that its edge does not flicker;
//   3. the subject is put over the new background (a colour, a gradient, a picture, the old background blurred, or a green screen), with a soft edge.
import { Upscaler, program } from "../upscale/gl";
import { chromaOf, coverRect, gradientAxis, hexToRgb } from "./backgrounds";
import { loadSegmenter, segment } from "./segment";

const MASK = `#version 300 es
precision highp float;
uniform sampler2D src; uniform sampler2D ai; uniform sampler2D prev;
uniform vec2 px; uniform vec4 chan; uniform float flipAi; uniform float useAi; uniform float refine; uniform float smoothT; uniform float hasPrev; uniform float reach;
uniform vec2 keyC; uniform float keyTol; uniform float keySoft;
in vec2 uv; out vec4 o;
vec2 chroma(vec3 c) { float y = dot(c, vec3(0.299, 0.587, 0.114)); return vec2((c.b - y) * 0.565, (c.r - y) * 0.713) / (y + 0.08); }
// the model's picture: a bitmap comes the other way up from a video frame (WebGL cannot flip a bitmap on upload), so it is read upside down then
float aiAt(vec2 q) { return dot(texture(ai, vec2(q.x, mix(q.y, 1.0 - q.y, flipAi))), chan); }
void main() {
  vec3 c = texture(src, uv).rgb;
  float m;
  if (useAi > 0.5) {
    m = aiAt(uv);
    if (refine > 0.01) {
      // two scales of a colour-guided filter: a pixel takes the mask of the pixels around it that look like it (so a bit of wall that the model included is pulled to "background"
      // by the wall around it, and hair is kept by the hair). Near first, for the edge itself; then far, for the model's mistakes of several pixels.
      float sharp = 0.003 + 0.04 * (1.0 - refine);
      float acc = m; float ws = 1.0;
      for (int j = -2; j <= 2; j++) for (int i = -2; i <= 2; i++) {
        if (i == 0 && j == 0) continue;
        vec2 q = uv + vec2(float(i), float(j)) * px * reach;
        vec3 d = texture(src, q).rgb - c;
        float w = exp(-dot(d, d) / sharp) * exp(-float(i * i + j * j) * 0.12);
        acc += aiAt(q) * w; ws += w;
      }
      float near = acc / ws;
      float acc2 = near; float ws2 = 1.0;
      for (int j = -3; j <= 3; j++) for (int i = -3; i <= 3; i++) {
        if (i == 0 && j == 0) continue;
        vec2 q = uv + vec2(float(i), float(j)) * px * reach * 4.0;
        vec3 d = texture(src, q).rgb - c;
        float w = exp(-dot(d, d) / sharp) * exp(-float(i * i + j * j) * 0.05);
        acc2 += aiAt(q) * w; ws2 += w;
      }
      m = mix(m, mix(near, acc2 / ws2, 0.85), refine);
    }
  } else {
    float d = distance(chroma(c), keyC);
    m = smoothstep(keyTol, keyTol + keySoft + 0.001, d);
  }
  float p = texture(prev, uv).r;
  m = mix(m, p, smoothT * hasPrev);
  o = vec4(m, m, m, 1.0);
}`;

// a small, blurred copy of the video (for the "blur the old background" look): one direction at a time
const BLUR = `#version 300 es
precision highp float;
uniform sampler2D tex; uniform vec2 dir;
in vec2 uv; out vec4 o;
void main() {
  vec3 acc = vec3(0.0); float ws = 0.0;
  for (int i = -6; i <= 6; i++) {
    float w = exp(-float(i * i) / 18.0);
    acc += texture(tex, uv + dir * float(i)).rgb * w; ws += w;
  }
  o = vec4(acc / ws, 1.0);
}`;

const COMPOSE = `#version 300 es
precision highp float;
uniform sampler2D src; uniform sampler2D mask; uniform sampler2D bgImg; uniform sampler2D bgBlur;
uniform float bgType; uniform vec3 c1; uniform vec3 c2; uniform vec2 gDir; uniform vec4 cover;
uniform float th; uniform float soft; uniform float view; uniform float isKey; uniform vec3 keyRGB; uniform float spill;
in vec2 uv; out vec4 o;
void main() {
  vec3 c = texture(src, uv).rgb;
  float m = texture(mask, uv).r;
  float a = smoothstep(th - soft, th + soft, m);
  if (isKey > 0.5 && spill > 0.01) {                                           // light of the screen that fell on the subject: its screen colour is pulled back to the others
    if (keyRGB.g >= keyRGB.r && keyRGB.g >= keyRGB.b) c.g = min(c.g, mix(c.g, 0.5 * (c.r + c.b), spill));
    else if (keyRGB.b >= keyRGB.r) c.b = min(c.b, mix(c.b, 0.5 * (c.r + c.g), spill));
    else c.r = min(c.r, mix(c.r, 0.5 * (c.g + c.b), spill));
  }
  vec3 bg;
  if (bgType < 0.5) bg = c1;
  else if (bgType < 1.5) { float t = clamp(0.5 + dot(uv - 0.5, gDir), 0.0, 1.0); bg = mix(c1, c2, smoothstep(0.0, 1.0, t)); }
  else if (bgType < 2.5) bg = texture(bgImg, cover.xy + uv * cover.zw).rgb;
  else if (bgType < 3.5) bg = texture(bgBlur, uv).rgb;
  else bg = vec3(0.0, 1.0, 0.0);
  vec3 fg = mix(c, bg, (1.0 - a) * 0.3 * step(0.002, a));                       // the edge pixels still carry some of the old background: pulled towards the new one
  vec3 res = mix(bg, fg, a);
  if (view > 0.5) res = vec3(a);
  o = vec4(res, 1.0);
}`;

const TYPES = { color: 0, gradient: 1, image: 2, blur: 3, green: 4 };

export class BgEngine extends Upscaler {
  constructor(canvas) {
    super(canvas);
    this.maskProg = program(this.gl, MASK);
    this.blurProg = program(this.gl, BLUR);
    this.composeProg = program(this.gl, COMPOSE);
    this.aiTex = this.texture("linear");
    this.imgTex = this.texture("linear");
    this.imgSource = null;
    this.imgSize = [1, 1];
    this.chan = [1, 0, 0, 0];
    this.chanKnown = false;
    this.flipAi = 0;
    this.inflight = null;
    this.failure = null;
    this.maskTime = -10;
    this.hasAi = false;
    this.latest = 0;
    this.lastTime = -1;
    this.histSize = [0, 0];
  }

  setOutput() { /* the picture keeps the video's own size: render() sets it */ }

  // The AI mask. While a video plays, the AI runs in the background on one frame after another and every frame of the video is drawn at once with the newest mask it has: the video
  // keeps its frame rate on a slow computer, and the mask is a few frames behind at most. A paused or jumped-to frame, and `look.exact`, wait for the mask of that very frame.
  // Nothing to do for a green screen.
  async prepare(video, look) {
    if (look.mode !== "ai") return;
    await loadSegmenter(video.videoWidth >= video.videoHeight);
    if (this.failure) { const e = this.failure; this.failure = null; throw e; }
    const jumped = Math.abs(video.currentTime - this.maskTime) > 0.2;
    if (look.exact || video.paused || jumped || !this.hasAi) {
      while (this.inflight) await this.inflight;
      await this.segmentNow(video);
      return;
    }
    if (!this.inflight) {
      this.inflight = this.segmentNow(video).catch((e) => { this.failure = e; }).finally(() => { this.inflight = null; });
    }
  }

  async segmentNow(video) {
    const mask = await segment(video);
    const { gl } = this;
    const bitmap = typeof ImageBitmap !== "undefined" && mask instanceof ImageBitmap;
    this.flipAi = bitmap ? 1 : 0;
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, !bitmap);
    gl.bindTexture(gl.TEXTURE_2D, this.aiTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, mask);
    if (!this.chanKnown) this.findChannel(mask);
    this.maskTime = video.currentTime;
    this.hasAi = true;
  }

  // Which channel of the model's picture holds the mask (its red, or its transparency): the one that varies.
  findChannel(mask) {
    try {
      const probe = document.createElement("canvas");
      probe.width = 32;
      probe.height = 32;
      const ctx = probe.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(mask, 0, 0, 32, 32);
      const d = ctx.getImageData(0, 0, 32, 32).data;
      const range = (k) => { let lo = 255; let hi = 0; for (let i = k; i < d.length; i += 4) { lo = Math.min(lo, d[i]); hi = Math.max(hi, d[i]); } return hi - lo; };
      this.chan = range(0) >= range(3) ? [1, 0, 0, 0] : [0, 0, 0, 1];
      this.chanKnown = true;
    } catch { this.chan = [1, 0, 0, 0]; }
  }

  uploadImage(image) {
    if (this.imgSource === image) return;
    const { gl } = this;
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.bindTexture(gl.TEXTURE_2D, this.imgTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    this.imgSource = image;
    this.imgSize = [image.naturalWidth || image.videoWidth || image.width || 1, image.naturalHeight || image.videoHeight || image.height || 1];
  }

  // look = { mode: "ai" | "key", view: "result" | "mask", bg: { type, color, color2, angle, image }, matte: { grow, feather, smooth, refine }, key: { color, tolerance, softness, spill } }
  render(source, look) {
    const { gl } = this;
    const sw = source.videoWidth || source.naturalWidth || source.width;
    const sh = source.videoHeight || source.naturalHeight || source.height;
    if (!sw || !sh) return;
    const ow = sw - (sw % 2);                                                    // video files want an even size
    const oh = sh - (sh % 2);
    if (this.canvas.width !== ow || this.canvas.height !== oh) super.setOutput(ow, oh);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.bindTexture(gl.TEXTURE_2D, this.srcTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    const ai = look.mode === "ai";
    const matte = look.matte;
    const key = look.key;

    const time = typeof source.currentTime === "number" ? source.currentTime : 0;
    const dt = time - this.lastTime;
    const same = this.histSize[0] === sw && this.histSize[1] === sh;
    const fresh = !same || this.lastTime < 0 || dt < -0.0005 || dt > 0.25;
    const advance = !fresh && dt > 0.0005;
    if (advance) this.latest ^= 1;
    const names = ["maskA", "maskB"];
    const newest = this.target(names[this.latest], sw, sh);
    const older = this.target(names[this.latest ^ 1], sw, sh);
    this.histSize = [sw, sh];
    if (fresh || advance) this.lastTime = time;

    const keyRGB = hexToRgb(key.color);
    const [kc0, kc1] = chromaOf(keyRGB);
    this.pass(this.maskProg, newest, [["src", this.srcTexture], ["ai", this.aiTex], ["prev", older.tex]], {
      px: [1 / sw, 1 / sh], chan: this.chan, flipAi: this.flipAi, useAi: ai && this.hasAi ? 1 : 0, refine: matte.refine, smoothT: ai ? matte.smooth : 0, hasPrev: fresh ? 0 : 1, reach: Math.max(1, sh / 300),
      keyC: [kc0, kc1], keyTol: key.tolerance, keySoft: key.softness,
    }, sw, sh);

    const bg = look.bg;
    const type = TYPES[bg.type] ?? 0;
    let blurTex = this.srcTexture;
    if (type === 3) {                                                          // the old background, blurred: a quarter of the size, one direction at a time
      const bw = Math.max(2, Math.round(sw / 4));
      const bh = Math.max(2, Math.round(sh / 4));
      const strength = 1 + 3 * (bg.blur ?? 0.5);
      const a = this.target("blurA", bw, bh, "linear");
      const b = this.target("blurB", bw, bh, "linear");
      this.pass(this.blurProg, a, [["tex", this.srcTexture]], { dir: [(strength * 1.5) / sw, 0] }, bw, bh);
      this.pass(this.blurProg, b, [["tex", a.tex]], { dir: [0, (strength * 1.5) / sh] }, bw, bh);
      this.pass(this.blurProg, a, [["tex", b.tex]], { dir: [(strength * 1.5) / sw, 0] }, bw, bh);
      this.pass(this.blurProg, b, [["tex", a.tex]], { dir: [0, (strength * 1.5) / sh] }, bw, bh);
      blurTex = b.tex;
    }
    if (type === 2 && bg.image) this.uploadImage(bg.image);
    const rect = coverRect(this.imgSize[0], this.imgSize[1], sw, sh);
    const axis = gradientAxis(bg.angle ?? 160);
    const norm = Math.abs(axis[0]) + Math.abs(axis[1]) || 1;
    const c1 = hexToRgb(bg.color);
    const c2 = hexToRgb(bg.color2 || bg.color);
    this.pass(this.composeProg, null, [["src", this.srcTexture], ["mask", newest.tex], ["bgImg", this.imgTex], ["bgBlur", blurTex]], {
      bgType: type, th: 0.5 - 0.22 * (matte.grow || 0), soft: 0.03 + 0.4 * matte.feather, view: look.view === "mask" ? 1 : 0, isKey: ai ? 0 : 1, spill: ai ? 0 : key.spill,
      gDir: [axis[0] / norm, -axis[1] / norm], cover: [rect.x, rect.y, rect.w, rect.h], c1, c2, keyRGB,
    }, ow, oh);
  }

  dispose() {
    this.gl.deleteTexture(this.aiTex);
    this.gl.deleteTexture(this.imgTex);
    super.dispose();
  }
}
